import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { toast } from "react-toastify";
import { ClinicImages } from "./components/ClinicImages";
import { ClinicInfo } from "./components/ClinicInfo";
import { AboutUs } from "./components/AboutUs";
import { Treatments } from "./components/Treatments";
import { Doctors } from "./components/Doctors";
import { setClinicData } from "../../../redux/bookclinic/bookclinicaction";
import baseUrl from "../../../api/baseUrl";
import {
  IClinicImage,
  IClinicInfo,
  ITreatment,
  ISubTreatment,
  IDoctor,
} from "./types/clinic.types";

function normalizeBookTreatment(raw: any): ITreatment {
  const subs: ISubTreatment[] = (raw.subTreatments || raw.subTreatment || []).map(
    (s: any) => ({
      id: String(s.id ?? s._id ?? ""),
      subTreatment: s.subTreatment ?? "",
      description: s.description ?? "",
      localPrice: Number(s.localPrice ?? s.price ?? 0),
      foreignerPrice: Number(s.foreignerPrice ?? s.foreginPrice ?? 0),
      minlocalPrice: Number(s.minlocalPrice ?? s.minPrice ?? 0),
      maxlocalPrice: Number(s.maxlocalPrice ?? s.maxPrice ?? 0),
      minforeignerPrice: Number(
        s.minforeignerPrice ?? s.minForeginPrice ?? 0
      ),
      maxforeignerPrice: Number(
        s.maxforeignerPrice ?? s.maxForeginPrice ?? 0
      ),
      duration: Number(s.duration ?? 0),
      note: s.note ?? "",
    })
  );
  const tid = raw?.treatmentId;
  const treatmentId =
    tid == null
      ? undefined
      : typeof tid === "object" && tid !== null && "_id" in tid
        ? String((tid as { _id: unknown })._id)
        : String(tid);
  return {
    ...raw,
    id: String(raw.id ?? raw._id ?? ""),
    treatmentId,
    subTreatments: subs,
  };
}

type SectionKey = "images" | "info" | "about" | "treatments" | "doctors";

const SECTIONS: {
  key: SectionKey;
  label: string;
  hint: string;
  icon: React.ReactNode;
}[] = [
  {
    key: "images",
    label: "Gallery",
    hint: "Showcase your clinic with high-quality photos",
    icon: <PhotoLibraryOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: "info",
    label: "Clinic details",
    hint: "Name, location, hours and contact information",
    icon: <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: "about",
    label: "About us",
    hint: "Tell patients what makes your clinic unique",
    icon: <ArticleOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: "treatments",
    label: "Treatments",
    hint: "Services, pricing and sub-treatments",
    icon: <MedicalServicesOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: "doctors",
    label: "Team",
    hint: "Your bookable dentists (assignments live in Treatments)",
    icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
  },
];

function SectionShell({
  index,
  label,
  hint,
  icon,
  children,
}: {
  index: number;
  label: string;
  hint: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="group scroll-mt-28" id={`clinic-section-${index}`}>
      <div className="mb-3 flex items-start gap-3 sm:mb-4 sm:gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primaryLight text-white shadow-md shadow-primary/25 sm:h-11 sm:w-11">
          {icon}
        </div>
        <div className="min-w-0 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-primary/70">
              Step {String(index).padStart(2, "0")}
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" />
            <h2 className="text-sm font-bold text-slate-900 sm:text-base">{label}</h2>
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500 sm:text-[0.8125rem]">
            {hint}
          </p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] ring-1 ring-slate-900/[0.03] transition-shadow duration-300 group-hover:shadow-[0_12px_40px_rgba(15,23,42,0.08)] [&>section]:rounded-2xl [&>section]:border-0 [&>section]:shadow-none [&>section]:p-4 [&>section]:sm:p-5 [&>section>div:first-child>h2]:hidden [&>section>div:first-child]:justify-end [&>section>div:first-child]:border-slate-100">
        {children}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-[60vh] bg-[#f4f7fb] px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl animate-pulse space-y-6">
        <div className="h-44 rounded-3xl bg-slate-200/80" />
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-white shadow-sm" />
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-56 rounded-2xl bg-white shadow-sm" />
        ))}
      </div>
    </div>
  );
}

const ClinicManagementPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [images, setImages] = useState<IClinicImage[]>([]);
  const [info, setInfo] = useState<IClinicInfo | null>(null);
  const [about, setAbout] = useState<string>("");
  const [treatments, setTreatments] = useState<ITreatment[]>([]);
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findBookClinicId")
      .then((response) => {
        const clinicData = response?.data?.data;

        if (clinicData) {
          const normalizedImages = (clinicData.images || []).map((img: any) => ({
            ...img,
            imageUrl: img.imageUrl || img.url || img.image || "",
            url: img.url || img.imageUrl || img.image || "",
            id: img.id || img._id || `img_${Date.now()}_${Math.random()}`,
          }));

          setImages(normalizedImages);
          setInfo({
            ...clinicData,
            location: clinicData.location || {
              addressLine: "",
              street: "",
              district: "",
              city: "",
              map: "",
            },
          });
          setAbout(clinicData.about || "");
          setTreatments(
            (clinicData.bookTreatment || []).map(normalizeBookTreatment)
          );
          setStatus(clinicData.status || "draft");
          setClinicData(clinicData);
        } else {
          setImages([]);
          setInfo(null);
          setAbout("");
          setTreatments([]);
          setStatus("draft");
        }
      })
      .catch((error) => {
        console.error("Error loading clinic data:", error);
        setImages([]);
        setInfo(null);
        setAbout("");
        setTreatments([]);
        setStatus("draft");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findEmployee", {
        headers: { status: true },
      })
      .then((response) => {
        const clinicData = response?.data?.data;
        setDoctors(clinicData || []);
      })
      .catch((error) => {
        console.error("Error loading doctors:", error);
        setDoctors([]);
      })
      .finally(() => {
        setIsLoadingDoctors(false);
      });
  }, []);

  const handleImagesChange = (newImages: IClinicImage[]) => setImages(newImages);
  const handleInfoChange = (newInfo: IClinicInfo) => setInfo(newInfo);
  const handleAboutChange = (newAbout: string) => setAbout(newAbout);
  const handleTreatmentsChange = (newTreatments: ITreatment[]) =>
    setTreatments(newTreatments);
  const handleDoctorsChange = (newDoctors: IDoctor[]) => setDoctors(newDoctors);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await baseUrl.patch("/client/booking/statusBookClinic", {
        status: "published",
      });
      if (response.data?.success) {
        setStatus("published");
        toast.success("Clinic published successfully!");
      } else {
        toast.error("Failed to publish clinic");
      }
    } catch (error: any) {
      console.error("Error publishing clinic:", error);
      toast.error(error?.response?.data?.message || "Failed to publish clinic");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDraft = async () => {
    setIsDrafting(true);
    try {
      const response = await baseUrl.patch("/client/booking/statusBookClinic", {
        status: "draft",
      });
      if (response.data?.success) {
        setStatus("draft");
        toast.success("Clinic moved to draft successfully!");
      } else {
        toast.error("Failed to move clinic to draft");
      }
    } catch (error: any) {
      console.error("Error moving clinic to draft:", error);
      toast.error(
        error?.response?.data?.message || "Failed to move clinic to draft"
      );
    } finally {
      setIsDrafting(false);
    }
  };

  const isPublished = status === "published";
  const subTreatmentCount = treatments.reduce(
    (sum, t) => sum + (t.subTreatments?.length ?? 0),
    0
  );

  const stats = [
    { label: "Photos", value: images.length },
    { label: "Treatments", value: treatments.length },
    { label: "Doctors", value: doctors.length },
  ];

  const renderSection = (key: SectionKey) => {
    switch (key) {
      case "images":
        return <ClinicImages images={images} onImagesChange={handleImagesChange} />;
      case "info":
        return <ClinicInfo info={info} onInfoChange={handleInfoChange} />;
      case "about":
        return <AboutUs aboutContent={about} onAboutChange={handleAboutChange} />;
      case "treatments":
        return (
          <Treatments
            treatments={treatments}
            onTreatmentsChange={handleTreatmentsChange}
            doctors={doctors}
            onDoctorsChange={handleDoctorsChange}
          />
        );
      case "doctors":
        return (
          <Doctors
            doctors={doctors}
            treatments={treatments}
            onDoctorsChange={handleDoctorsChange}
          />
        );
    }
  };

  if (isLoading || isLoadingDoctors) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-full w-full min-w-0 bg-[#f4f7fb]">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-[#1a5f96] to-primaryDark text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-sky-300/20 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-4 pb-10 pt-8 sm:px-6 sm:pb-12 sm:pt-10 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur-sm">
                {isPublished ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    Live on booking
                  </>
                ) : (
                  <>
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />
                    Draft mode
                  </>
                )}
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-[2rem] lg:leading-tight">
                {info?.name?.trim() || "Clinic booking profile"}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-white/80 sm:text-[0.9375rem]">
                Build a polished public profile for online bookings. Save each section
                as you go, then publish when everything looks right.
              </p>
            </div>

            <div className="grid w-full grid-cols-3 gap-2 sm:gap-3 lg:max-w-md lg:shrink-0">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/15 bg-white/10 p-3 text-center backdrop-blur-md sm:p-4"
                >
                  <p className="text-xl font-bold tabular-nums sm:text-2xl">{stat.value}</p>
                  <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-white/70 sm:text-[11px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick nav */}
      <div className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto max-w-6xl overflow-x-auto px-4 sm:px-6 lg:px-8">
          <nav
            className="flex gap-1 py-2.5 sm:gap-2"
            aria-label="Profile sections"
          >
            {SECTIONS.map((section, i) => (
              <a
                key={section.key}
                href={`#clinic-section-${i + 1}`}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary sm:px-4 sm:text-[0.8125rem]"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-primary">
                  {i + 1}
                </span>
                {section.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="flex min-w-0 flex-col gap-8 lg:gap-10" role="main">
          {SECTIONS.map((section, i) => (
            <SectionShell
              key={section.key}
              index={i + 1}
              label={section.label}
              hint={section.hint}
              icon={section.icon}
            >
              {renderSection(section.key)}
            </SectionShell>
          ))}
        </div>

        {/* Publish panel */}
        <section
          className="sticky bottom-4 z-20 mt-10 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.12)] ring-1 ring-slate-900/[0.04] sm:bottom-6"
          aria-label="Publish status"
        >
          <div
            className={`h-1 w-full bg-gradient-to-r ${
              isPublished
                ? "from-emerald-400 via-emerald-500 to-teal-500"
                : "from-amber-400 via-orange-400 to-amber-500"
            }`}
          />
          <div className="flex flex-col gap-5 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 lg:p-6">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  isPublished
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-amber-50 text-amber-600"
                }`}
              >
                {isPublished ? (
                  <VisibilityOutlinedIcon sx={{ fontSize: 22 }} />
                ) : (
                  <VisibilityOffOutlinedIcon sx={{ fontSize: 22 }} />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-900 sm:text-base">
                    {isPublished ? "Your clinic is live" : "Ready to go live?"}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      isPublished
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {isPublished ? (
                      <>
                        <CheckCircleIcon sx={{ fontSize: 12 }} />
                        Published
                      </>
                    ) : (
                      "Draft"
                    )}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-500 sm:text-sm">
                  {isPublished
                    ? "Patients can discover and book your clinic on the public site."
                    : `Complete your profile (${images.length} photos, ${treatments.length} treatments, ${subTreatmentCount} sub-services, ${doctors.length} doctors) then publish.`}
                </p>
              </div>
            </div>

            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
              {isPublished ? (
                <button
                  type="button"
                  onClick={handleDraft}
                  disabled={isDrafting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-3 text-sm font-semibold text-amber-800 transition-all hover:border-amber-400 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[200px] sm:w-auto"
                >
                  {isDrafting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <EditIcon sx={{ fontSize: 18 }} />
                  )}
                  {isDrafting ? "Moving to draft…" : "Unpublish"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={isPublishing}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primaryLight px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:shadow-xl hover:shadow-primary/35 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[220px] sm:w-auto"
                >
                  {isPublishing ? (
                    <CircularProgress size={18} sx={{ color: "white" }} />
                  ) : (
                    <PublishIcon sx={{ fontSize: 18 }} />
                  )}
                  {isPublishing ? "Publishing…" : "Publish clinic"}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClinicManagementPage;
